/**
 * Sector grouping "brain" for the single-stock 2x leveraged ETF dropdown.
 *
 * Pure classification + grouping logic — no React, no fund data. Maps a fund's
 * `underlyingIndustry` (the reference stock's industry) into one of six fixed
 * sector buckets, then groups a fund list into those buckets (fixed order,
 * empty buckets omitted, sorted by live AUM within each bucket).
 *
 * The taxonomy below is classification CONFIG, not fund data — it never names
 * tickers or issuers, only industry → bucket mappings.
 */
import type { Fund } from "@/data/funds";
import { fundLiveAumDollars } from "@/hooks/use-fund-live-data";

export type SectorKey = "semis" | "software" | "crypto" | "mobility" | "energy" | "other";

/** The six sector buckets, in fixed display order. `anchorId` is the in-page scroll target. */
export const SECTOR_BUCKETS: { key: SectorKey; label: string; anchorId: string }[] = [
  { key: "semis", label: "Semiconductors & Hardware", anchorId: "sg-semis" },
  { key: "software", label: "Software, Internet & AI", anchorId: "sg-software" },
  { key: "crypto", label: "Crypto & Fintech", anchorId: "sg-crypto" },
  { key: "mobility", label: "Mobility, Aerospace & Quantum", anchorId: "sg-mobility" },
  { key: "energy", label: "Energy & Power", anchorId: "sg-energy" },
  { key: "other", label: "Consumer, Healthcare & Other", anchorId: "sg-other" },
];

/**
 * Lowercased `underlyingIndustry` → bucket. Keys MUST be lowercase; lookups
 * lowercase + trim before indexing. "other" entries are belt-and-suspenders —
 * unmapped industries fall through to the "other" catch-all default anyway.
 */
export const INDUSTRY_TO_BUCKET: Record<string, SectorKey> = {
  // ── Semiconductors & Hardware ──────────────────────────────────────────────
  semiconductor: "semis",
  "semiconductor and photonics": "semis",
  "server and data center infrastructure": "semis",
  "data center infrastructure": "semis",
  "data storage technology": "semis",
  "electronic test and measurement": "semis",

  // ── Software, Internet & AI ────────────────────────────────────────────────
  "internet platform and digital advertising": "software",
  "enterprise software and cloud computing": "software",
  "cloud computing and ai infrastructure": "software",
  "software and cloud computing": "software",
  "data analytics and ai software": "software",
  "mobile application technology and digital advertising": "software",
  cybersecurity: "software",

  // ── Crypto & Fintech ───────────────────────────────────────────────────────
  "digital asset": "crypto",
  "financial technology and brokerage": "crypto",
  "financial technology": "crypto",
  "american digital asset": "crypto",

  // ── Mobility, Aerospace & Quantum ──────────────────────────────────────────
  "aerospace and space technology": "mobility",
  "quantum computing": "mobility",
  "electric vehicle and automotive": "mobility",
  "wireless and drone technology": "mobility",

  // ── Energy & Power ─────────────────────────────────────────────────────────
  "nuclear and advanced energy": "energy",
  "energy technology": "energy",
  "energy storage technology": "energy",
  "american nuclear and advanced energy": "energy",

  // ── Consumer, Healthcare & Other (also the catch-all default) ──────────────
  "e-commerce and cloud computing": "other",
  "consumer electronics and technology": "other",
  "health care": "other",
  "streaming entertainment": "other",
  "diversified holding company": "other",
  "health technology and precision medicine": "other",
  "telehealth and digital health": "other",
  pharmaceutical: "other",
  "specialty retail": "other",
  "education technology": "other",
  beverage: "other",
  "land and natural resource management": "other",
  "safety and security technology": "other",
  "e-commerce and delivery technology": "other",
};

/** Classify a fund into its sector bucket via its `underlyingIndustry`; "other" is the default. */
export function bucketForFund(fund: Pick<Fund, "underlyingIndustry">): SectorKey {
  return INDUSTRY_TO_BUCKET[(fund.underlyingIndustry ?? "").toLowerCase().trim()] ?? "other";
}

/**
 * Group funds into the six sector buckets.
 *
 * Returns buckets in `SECTOR_BUCKETS` order, omitting any that end up empty.
 * Within each bucket, funds sort by live AUM descending (no-AUM funds last),
 * with ticker ascending as the tiebreak.
 */
export function groupBySector(
  funds: readonly Fund[],
): { key: SectorKey; label: string; anchorId: string; funds: Fund[] }[] {
  const byKey = new Map<SectorKey, Fund[]>();
  for (const fund of funds) {
    const key = bucketForFund(fund);
    const existing = byKey.get(key);
    if (existing) existing.push(fund);
    else byKey.set(key, [fund]);
  }

  const result: { key: SectorKey; label: string; anchorId: string; funds: Fund[] }[] = [];
  for (const bucket of SECTOR_BUCKETS) {
    const bucketFunds = byKey.get(bucket.key);
    if (!bucketFunds || bucketFunds.length === 0) continue;
    bucketFunds.sort((a, b) => {
      const aumDelta = (fundLiveAumDollars(b) ?? -1) - (fundLiveAumDollars(a) ?? -1);
      if (aumDelta !== 0) return aumDelta;
      return a.ticker.localeCompare(b.ticker);
    });
    result.push({ ...bucket, funds: bucketFunds });
  }
  return result;
}
